import { UserManager } from "oidc-client-ts";
import type {
  INavigator,
  IWindow,
  NavigateParams,
  NavigateResponse,
  StateStore,
} from "oidc-client-ts";
import type { OidcSettings } from "../app/config";
import {
  nativeAppId,
  nativeLoginCallbackPath,
  nativePostLogoutRedirectUri,
  nativeRedirectUri,
} from "./nativeApp";

/**
 * The system browser used for the login of the native apps (Capacitor `Browser` plugin:
 * `SFSafariViewController` on iOS, Custom Tabs on Android). The login form never runs in the
 * app's own WebView (RFC 8252, spec 9.1 and 12.2).
 */
export interface SystemBrowserPort {
  open(options: { readonly url: string }): Promise<void>;
  close(): Promise<void>;
}

/** Asynchronous key-value storage (Capacitor `Preferences` plugin; injected for tests). */
export interface KeyValuePort {
  get(options: { readonly key: string }): Promise<{ readonly value: string | null }>;
  set(options: { readonly key: string; readonly value: string }): Promise<void>;
  remove(options: { readonly key: string }): Promise<void>;
  keys(): Promise<{ readonly keys: readonly string[] }>;
}

/**
 * Navigator of `oidc-client-ts` that opens the authorization and end-session requests in the
 * system browser. The response comes back through the app URL scheme (see {@link appUrlToPath}).
 */
export class SystemBrowserNavigator implements INavigator {
  private readonly browser: SystemBrowserPort;

  /**
   * Creates the navigator.
   *
   * @param browser - The system browser.
   */
  constructor(browser: SystemBrowserPort) {
    this.browser = browser;
  }

  /** @inheritdoc */
  prepare(): Promise<IWindow> {
    const handle: IWindow = {
      navigate: async (params: NavigateParams): Promise<NavigateResponse> => {
        await this.browser.open({ url: params.url });
        const response: NavigateResponse = { url: params.url };

        return response;
      },
      close: (): void => {
        this.browser.close().catch(() => undefined);
      },
    };
    const prepared = Promise.resolve(handle);

    return prepared;
  }

  /** @inheritdoc */
  callback(): Promise<void> {
    // The response is processed by the login callback route, not by the navigator.
    const done = Promise.resolve();

    return done;
  }
}

/**
 * `oidc-client-ts` state store on top of the native key-value storage. The WebView storage of iOS
 * may be evicted under storage pressure; the native storage keeps the session of the app.
 */
export class KeyValueStateStore implements StateStore {
  private readonly storage: KeyValuePort;

  private readonly prefix: string;

  /**
   * Creates the store.
   *
   * @param storage - Native key-value storage.
   * @param prefix - Prefix of all keys of this store.
   */
  constructor(storage: KeyValuePort, prefix: string) {
    this.storage = storage;
    this.prefix = prefix;
  }

  /** @inheritdoc */
  async set(key: string, value: string): Promise<void> {
    await this.storage.set({ key: this.prefix + key, value });
  }

  /** @inheritdoc */
  async get(key: string): Promise<string | null> {
    const stored = await this.storage.get({ key: this.prefix + key });
    const value = stored.value;

    return value;
  }

  /** @inheritdoc */
  async remove(key: string): Promise<string | null> {
    const value = await this.get(key);
    await this.storage.remove({ key: this.prefix + key });

    return value;
  }

  /** @inheritdoc */
  async getAllKeys(): Promise<string[]> {
    const stored = await this.storage.keys();
    const keys = stored.keys
      .filter((key) => key.startsWith(this.prefix))
      .map((key) => key.slice(this.prefix.length));

    return keys;
  }
}

/** Key prefix of the session stored by the native apps. */
export const nativeSessionKeyPrefix = "oidc.";

/**
 * Creates the `UserManager` of the native apps: authorization code flow with PKCE in the system
 * browser, redirect through the private-use URI scheme and the session in native storage
 * (ADR-0035).
 *
 * @param settings - OIDC settings.
 * @param browser - The system browser.
 * @param storage - Native key-value storage for the session.
 * @returns The user manager.
 */
export function createNativeUserManager(
  settings: OidcSettings,
  browser: SystemBrowserPort,
  storage: KeyValuePort,
): UserManager {
  const navigator = new SystemBrowserNavigator(browser);
  const manager = new UserManager(
    {
      authority: settings.authority,
      client_id: settings.clientId,
      scope: settings.scope,
      response_type: "code",
      redirect_uri: nativeRedirectUri,
      post_logout_redirect_uri: nativePostLogoutRedirectUri,
      userStore: new KeyValueStateStore(storage, nativeSessionKeyPrefix),
      automaticSilentRenew: false,
      monitorSession: false,
    },
    navigator,
    navigator,
    navigator,
  );

  return manager;
}

/**
 * Maps a URL that opened the native app to an application path. Only the app's own URI scheme is
 * accepted; the login callback keeps its query (authorization response), every other path is
 * reduced to its path so that no foreign parameters reach the application.
 *
 * @param url - The URL delivered by the operating system.
 * @param appId - The app's URI scheme.
 * @returns The path, or `null` for a foreign URL.
 */
export function appUrlToPath(url: string, appId: string = nativeAppId): string | null {
  const prefix = `${appId}:`;

  if (!url.toLowerCase().startsWith(prefix.toLowerCase())) {
    return null;
  }

  const rest = url.slice(prefix.length).replace(/^\/+/u, "/");
  const normalized = rest.startsWith("/") ? rest : `/${rest}`;
  const parsed = URL.canParse(normalized, "https://app.invalid")
    ? new URL(normalized, "https://app.invalid")
    : null;
  let path: string | null = null;

  if (parsed?.origin === "https://app.invalid") {
    path =
      parsed.pathname === nativeLoginCallbackPath
        ? `${parsed.pathname}${parsed.search}`
        : parsed.pathname;
  }

  return path;
}
